import React from 'react';
import SEO from '../components/SEO';

const Privacy: React.FC = () => {
  return (
    <>
      <SEO
        title="Privacy Policy - GameObsidian"
        description="Read our privacy policy to understand how GameObsidian collects, uses, and protects your personal information."
        url="/privacy"
      />

      <div className="min-h-screen pt-20 pb-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold mb-8">
            Privacy <span className="text-gradient">Policy</span>
          </h1>
          <p className="text-dark-muted mb-8">Last updated: December 2025</p>

          <div className="glass-effect rounded-2xl p-8 space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Introduction</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                GameObsidian ("we," "our," or "us") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your 
                information when you visit our website gameobsidian.com.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Information We Collect</h2>
              <h3 className="text-xl font-semibold mb-2">2.1 Information You Provide</h3>
              <p className="text-dark-muted leading-relaxed mb-4">
                We may collect information that you voluntarily provide to us, including:
              </p>
              <ul className="list-disc list-inside text-dark-muted space-y-2 mb-4">
                <li>Name and contact information</li>
                <li>Email address</li>
                <li>Any other information you choose to provide</li>
              </ul>

              <h3 className="text-xl font-semibold mb-2">2.2 Automatically Collected Information</h3>
              <p className="text-dark-muted leading-relaxed mb-4">
                When you visit our website, we may automatically collect certain information, including:
              </p>
              <ul className="list-disc list-inside text-dark-muted space-y-2 mb-4">
                <li>IP address</li>
                <li>Browser type and version</li>
                <li>Operating system</li>
                <li>Pages visited and time spent on pages</li>
                <li>Referring website addresses</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. How We Use Your Information</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside text-dark-muted space-y-2 mb-4">
                <li>Provide, maintain, and improve our services</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Send you promotional communications (with your consent)</li>
                <li>Analyze usage patterns and trends</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Cookies and Tracking Technologies</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                We use cookies and similar tracking technologies to track activity on our website 
                and store certain information. You can instruct your browser to refuse all cookies 
                or to indicate when a cookie is being sent. For more information, please see our 
                Cookie Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Information Sharing and Disclosure</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                We do not sell your personal information. We may share your information in the following circumstances:
              </p>
              <ul className="list-disc list-inside text-dark-muted space-y-2 mb-4">
                <li>With service providers who assist us in operating our website</li>
                <li>When required by law or to protect our rights</li>
                <li>In connection with a business transfer or merger</li>
                <li>With your consent</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Data Security</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal 
                information. However, no method of transmission over the Internet or electronic storage 
                is 100% secure, and we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Your Rights</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                Under UK data protection laws, you have the right to:
              </p>
              <ul className="list-disc list-inside text-dark-muted space-y-2 mb-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to processing of your information</li>
                <li>Request restriction of processing</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Third-Party Links</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                Our website may contain links to third-party websites. We are not responsible for 
                the privacy practices of these external sites. We encourage you to review their 
                privacy policies.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Children's Privacy</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                Our services are intended for users aged 18 and over. We do not knowingly collect 
                personal information from individuals under 18 years of age.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Changes to This Policy</h2>
              <p className="text-dark-muted leading-relaxed mb-4">
                We may update this Privacy Policy from time to time. We will notify you of any 
                changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Contact Us</h2>
              <p className="text-dark-muted leading-relaxed">
                If you have any questions about this Privacy Policy, please contact us at:{' '}
                <a
                  href="mailto:privacy@gameobsidian.com"
                  className="text-accent-primary hover:text-accent-secondary transition-colors"
                >
                  privacy@gameobsidian.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default Privacy;

